"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { PhoneCall, Truck, Search, FileText, CircleCheck as CheckCircle2 } from 'lucide-react';
export default function TheDeploymentProcess() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const steps = [{
    number: "01",
    title: "Initial Consultation",
    description: "We analyze your current business bottlenecks and identify exactly where you are losing potential revenue.",
    tag: "[ DISCOVERY PHASE ]",
    icon: PhoneCall
  }, {
    number: "02",
    title: "Strategy Deployment",
    description: "Our team configures your custom AI answering and booking systems to align with your brand voice.",
    tag: "[ SYSTEM SETUP ]",
    icon: Truck
  }, {
    number: "03",
    title: "Performance Audit",
    description: "We test the integration to ensure every lead is captured and every appointment is scheduled perfectly.",
    tag: "[ QUALITY ASSURANCE ]",
    icon: Search
  }, {
    number: "04",
    title: "Launch & Integration",
    description: "Your new tactical service goes live, seamlessly connecting your website to our 24/7 booking engine.",
    tag: "[ SYSTEM ACTIVATION ]",
    icon: FileText
  }, {
    number: "05",
    title: "Growth Optimization",
    description: "We monitor incoming data to refine your automated workflows and maximize your booking conversion rates.",
    tag: "[ CONTINUOUS IMPROVEMENT ]",
    icon: CheckCircle2
  }];
  return <section data-section-id="858" id="the-deployment-process" className="bg-background text-foreground py-32 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="max-w-6xl mx-auto relative">
        {}
        <div ref={headerRef} className={`text-center mb-20 lg:mb-28 transition-all duration-1000 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs sm:text-sm tracking-[0.2em] mb-6" style={{
          color: "var(--chart-2)"
        }}>
            [ THE DEPLOYMENT PROCESS ]
          </p>
          <h2 className="font-default text-4xl sm:text-5xl lg:text-6xl leading-tight text-foreground max-w-3xl mx-auto">
            Five steps to{" "}
            <em className="font-default italic" style={{
            color: "var(--chart-2)"
          }}><br />automate your business</em>
          </h2>
        </div>

        {}
        <div ref={lineRef} className="relative">
          {}
          <div className="absolute left-8 lg:left-1/2 top-0 bottom-0 w-px lg:-translate-x-1/2 bg-border/30" aria-hidden="true" />
          <div className="absolute left-8 lg:left-1/2 top-0 w-px lg:-translate-x-1/2 transition-all duration-[2000ms] ease-out" style={{
          backgroundColor: "var(--chart-2)",
          height: lineVisible ? "100%" : "0%"
        }} aria-hidden="true" />

          <div className="space-y-16 lg:space-y-24">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isLeft = i % 2 === 0;
            return <TimelineStep key={i} data-index={i} step={step} index={i} isLeft={isLeft} Icon={Icon} />;
          })}
          </div>
        </div>
      </div>
    </section>;
}
function TimelineStep({
  step,
  index,
  isLeft,
  Icon,
  ...rest
}: {
  step: {
    number: string;
    title: string;
    description: string;
    tag: string;
  };
  index: number;
  isLeft: boolean;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  [key: string]: unknown;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  return <div ref={ref} {...rest} className={`relative transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      {}
      <div className="absolute left-8 lg:left-1/2 top-8 lg:top-12 -translate-x-1/2 z-10">
        <div className="relative flex items-center justify-center">
          <div className="absolute w-6 h-6 rounded-lg opacity-30 animate-ping" style={{
          backgroundColor: "var(--chart-2)"
        }} />
          <div className="w-3 h-3 rounded-lg relative" style={{
          backgroundColor: "var(--chart-2)"
        }} />
        </div>
      </div>

      {}
      <div className="lg:hidden pl-20">
        <div className="mb-4">
          <span className="font-default text-6xl opacity-40 leading-none" style={{
          color: "var(--chart-2)"
        }}>
            {step.number}
          </span>
        </div>
        <StepCard step={step} Icon={Icon} />
      </div>

      {}
      <div className="hidden lg:grid lg:grid-cols-2 lg:gap-16 items-center">
        {isLeft ? <>
            <div className="flex justify-end pr-12">
              <StepCard step={step} Icon={Icon} />
            </div>
            <div className="flex items-center pl-12">
              <span className="font-default leading-none opacity-40" style={{
            color: "var(--chart-2)",
            fontSize: "6rem"
          }}>
                {step.number}
              </span>
            </div>
          </> : <>
            <div className="flex items-center justify-end pr-12">
              <span className="font-default leading-none opacity-40" style={{
            color: "var(--chart-2)",
            fontSize: "6rem"
          }}>
                {step.number}
              </span>
            </div>
            <div className="flex justify-start pl-12">
              <StepCard step={step} Icon={Icon} />
            </div>
          </>}
      </div>
    </div>;
}
function StepCard({
  step,
  Icon
}: {
  step: {
    number: string;
    title: string;
    description: string;
    tag: string;
  };
  Icon: React.ComponentType<{
    className?: string;
  }>;
}) {
  return <div className="bg-card text-card-foreground rounded-lg p-8 shadow-lg border border-border">
      <div className="flex items-start gap-4 mb-4">
        <div className="flex items-center justify-center w-10 h-10 rounded-md shrink-0 border border-border" style={{
        backgroundColor: "var(--accent)",
        color: "var(--chart-2)"
      }}>
          <Icon className="w-5 h-5" />
        </div>
        <h3 className="font-default text-2xl lg:text-[1.75rem] leading-tight pt-1">
          {step.title}
        </h3>
      </div>
      <p className="font-default text-base leading-relaxed mb-5 text-muted-foreground">
        {step.description}
      </p>
      <p className="font-mono text-xs tracking-[0.15em]" style={{
      color: "var(--chart-2)"
    }}>
        {step.tag}
      </p>
    </div>;
}